import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { BasePage } from 'pages/BasePage';
import { CompanyDetailBox } from 'company/components/CompanyDetailBox';
import { PersonalDetailBox } from 'user/components/PersonalDetailBox';
import { CreatePasswordDetailBox } from 'auth/components/CreatePasswordDetailBox';
import { Link } from 'common/components/Link';
import { useAuth } from 'auth/hooks';
import { getIntlPath } from 'common/utils';
import { getUrlParamValue } from 'utils';

import { useFetchUserAndCompanyQuery } from '../../graphql/types';

interface IProps {
    className?: string;
}

const BaseCreateAccountPage = ({ className }: IProps) => {
    const [submitted, setSubmitted] = useState(false);

    const { isLoggedIn } = useAuth();
    const { t } = useTranslation();

    const uid = getUrlParamValue('uid') ?? '';
    const token = getUrlParamValue('token') ?? '';

    const { data, loading, error } = useFetchUserAndCompanyQuery({
        variables: {
            uid,
            token,
        },
    });

    if (isLoggedIn()) return <Redirect to={getIntlPath('login')} />;

    const { user } = data || {};

    const showAccountPage = !!uid && !!token && !!user;

    return (
        <BasePage
            basic
            className={className}
            loading={loading}
            pageTitle={
                submitted
                    ? t('account.create.pageTitleCreated')
                    : t('account.create.pageTitle')
            }
        >
            {!submitted && (
                <>
                    <Box className="pageDescription">
                        <Typography
                            color={showAccountPage ? 'initial' : 'error'}
                            variant="body2"
                        >
                            {showAccountPage
                                ? t('account.create.pageDescription')
                                : t('account.create.pageDescriptionInvalid')}
                        </Typography>
                    </Box>

                    {showAccountPage && (
                        <Box mt={12}>
                            <CompanyDetailBox
                                basic
                                companyData={{
                                    company: data?.user?.company,
                                    loading,
                                    error,
                                }}
                            />

                            <PersonalDetailBox
                                basic
                                userData={{
                                    user: data?.user,
                                    loading,
                                    error,
                                }}
                            />

                            <CreatePasswordDetailBox
                                token={token}
                                uid={uid}
                                onSubmit={() => setSubmitted(true)}
                            />
                        </Box>
                    )}
                </>
            )}

            {submitted && (
                <Box className="pageDescription">
                    {t('account.create.pageDescriptionSubmitted')}
                    <Link to={getIntlPath('login')}>
                        {t('account.create.login')}
                    </Link>
                </Box>
            )}
        </BasePage>
    );
};

export const CreateAccountPage = styled(BaseCreateAccountPage)``;
